import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import {
  ArcElement,
  BarElement,
  Chart,
  ChartConfiguration,
  ChartDataset,
  ChartType,
  Plugin,
  registerables,
  Scale,
  TooltipItem,
} from 'chart.js';
import {
  AlloChartSpec,
  buildChartAriaLabel,
  parseAlloChartSpec,
} from '../../../models/chart-card.types';
import { CHART_PALETTE, ChartPalette } from './chart-palette';

/**
 * This lib is published with `"sideEffects": false`, so a top-level
 * `Chart.register(...)` is a side effect a bundler is free to drop. Registering
 * from the constructor keeps it provably reachable; the guard keeps it to once
 * per bundle rather than once per chart.
 */
let chartJsRegistered = false;

/** Slices below this share of the total are too thin to carry a readable label. */
const MIN_LABEL_SHARE = 0.06;

/** Below this viewport width a right-hand donut legend crowds out the arcs. */
const NARROW_VIEWPORT = '(max-width: 560px)';

/** A bar chart flips horizontal past either of these — vertical ticks would clip. */
const LONG_LABEL_CHARS = 14;
const MANY_CATEGORIES = 6;

const VALUE_LABEL_FONT = "700 10px 'At Haüss', sans-serif";
const DONUT_LABEL_FONT = "700 11px 'At Haüss', sans-serif";

function formatAxisValue(value: number, vScale: Scale | undefined): string {
  return vScale ? vScale.getLabelForValue(value) : `${value}`;
}

export interface ChartTooltipItem {
  dataset: { label?: string };
  formattedValue: string;
}

export function buildChartTooltipLabel(
  item: ChartTooltipItem,
  unit: string
): string {
  const prefix = item.dataset.label ? `${item.dataset.label}: ` : '';
  return `${prefix}${item.formattedValue}${unit}`;
}

/** The expanded-chart dialog passes its spec through `MAT_DIALOG_DATA`. */
interface ChartDialogData {
  rawSpec: string;
  /** dialog-loader.module stubs MatDialogRef with {}, so the opener passes close. */
  close: () => void;
}

/**
 * Draws value labels directly on the canvas — replicating the role of
 * chartjs-plugin-datalabels without taking on the dependency.
 *
 * Bars get a bold value at the bar end (clamped inside the chart area); donut
 * arcs get a white bold percentage centred in the slice, but only when the
 * slice is wide enough to hold it.
 */
function valueLabelPlugin(spec: AlloChartSpec): Plugin {
  return {
    id: 'alloValueLabels',
    afterDatasetsDraw(chart) {
      const { ctx, chartArea } = chart;
      if (!ctx || !chartArea) return;

      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      chart.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);
        if (meta.hidden) return;

        const values = (dataset.data as number[]) ?? [];
        const total = values.reduce((sum, value) => sum + Math.abs(value), 0);

        meta.data.forEach((element, index) => {
          const value = values[index];
          if (typeof value !== 'number') return;

          if (element instanceof ArcElement) {
            // Donut: white percentage centred in arcs with room for it.
            const share = total > 0 ? Math.abs(value) / total : 0;
            if (share < MIN_LABEL_SHARE) return;

            const { x, y } = element.tooltipPosition(true);
            ctx.font = DONUT_LABEL_FONT;
            ctx.fillStyle = '#ffffff';
            ctx.fillText(`${Math.round(share * 100)}%`, x, y);
            return;
          }

          if (element instanceof BarElement) {
            const horizontal = chart.options.indexAxis === 'y';
            const props = element.getProps(['x', 'y'], true);
            ctx.font = VALUE_LABEL_FONT;
            ctx.fillStyle = chart.options.color as string;

            const text = `${formatAxisValue(value, meta.vScale)}${spec.unit}`;
            if (horizontal) {
              const width = ctx.measureText(text).width;
              const outsideX = props.x + width / 2 + 6;
              if (outsideX + width / 2 <= chartArea.right - 2) {
                ctx.fillText(text, outsideX, props.y);
              } else {
                // No room past the bar end — draw inside the bar in white.
                ctx.fillStyle = '#ffffff';
                ctx.fillText(text, props.x - width / 2 - 6, props.y);
              }
            } else {
              const y = Math.max(props.y - 9, chartArea.top + 7);
              ctx.fillText(text, props.x, y);
            }
          }
        });
      });

      ctx.restore();
    },
  };
}

@Component({
  selector: 'qt-ai-chat-chart-card',
  standalone: true,
  imports: [MatIconModule, TranslationCoreModule],
  templateUrl: './ai-chat-chart-card.component.html',
  styleUrls: ['./ai-chat-chart-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatChartCardComponent implements OnDestroy {
  /** Raw JSON string lifted from the `<chart>` block. */
  readonly rawSpec = input<string>('');

  private readonly canvasRef =
    viewChild<ElementRef<HTMLCanvasElement>>('chartCanvas');
  private readonly dialog = inject(MatDialog);

  /** Present only when this instance IS the expanded dialog. */
  private readonly dialogData = inject<ChartDialogData | null>(
    MAT_DIALOG_DATA,
    { optional: true }
  );

  /** The expanded variant drops the expand button and grows the canvas. */
  readonly isExpanded = !!this.dialogData;

  readonly spec = computed(() =>
    parseAlloChartSpec(this.dialogData?.rawSpec ?? this.rawSpec())
  );

  readonly ariaLabel = computed(() => {
    const spec = this.spec();
    return spec ? buildChartAriaLabel(spec) : '';
  });

  private chart: Chart | null = null;

  constructor() {
    if (!chartJsRegistered) {
      Chart.register(...registerables);
      chartJsRegistered = true;
    }

    effect(() => {
      const spec = this.spec();
      const canvas = this.canvasRef()?.nativeElement;

      this.destroyChart();
      if (!spec || !canvas) return;

      try {
        this.chart = this.createChart(canvas, spec, CHART_PALETTE);
      } catch (error) {
        // A canvas-less environment (or a hostile spec) must never break the
        // surrounding message — the template falls back to plain text.
        console.warn('Failed to render Allo chart:', error);
        this.chart = null;
      }
    });
    // No afterClosed hook: MatDialog destroys this component on close, so
    // ngOnDestroy already disposes the Chart instance. (MatDialogRef is also
    // stubbed globally by dialog-loader.module.ts, so it must not be injected.)
  }

  ngOnDestroy(): void {
    this.destroyChart();
  }

  openExpanded(): void {
    const raw = this.rawSpec();
    if (!raw) return;

    const data: ChartDialogData = { rawSpec: raw, close: () => undefined };
    const ref = this.dialog.open(AiChatChartCardComponent, {
      data,
      panelClass: 'qt-chart-dialog',
      width: '90vw',
      maxWidth: '760px',
      autoFocus: 'dialog',
    });
    data.close = () => ref.close();
  }

  closeExpanded(): void {
    this.dialogData?.close();
  }

  private destroyChart(): void {
    this.chart?.destroy();
    this.chart = null;
  }

  private isNarrowViewport(): boolean {
    return (
      typeof matchMedia === 'function' && matchMedia(NARROW_VIEWPORT).matches
    );
  }

  /**
   * Each chart type needs its own `ChartConfiguration<T>` — options like
   * `cutout` only exist on the doughnut variant — so the instance is built
   * per branch rather than through one widened config.
   */
  private createChart(
    canvas: HTMLCanvasElement,
    spec: AlloChartSpec,
    palette: ChartPalette
  ): Chart {
    const tooltip = this.buildTooltip(spec);

    if (spec.type === 'donut') {
      return new Chart(canvas, this.buildDonutConfig(spec, palette, tooltip));
    }
    if (spec.type === 'line') {
      return new Chart(canvas, this.buildLineConfig(spec, palette, tooltip));
    }
    return new Chart(canvas, this.buildBarConfig(spec, palette, tooltip));
  }

  /** Tooltip values carry the spec's unit suffix (e.g. `62%`). */
  private buildTooltip(spec: AlloChartSpec): object {
    return {
      callbacks: {
        label: (item: TooltipItem<ChartType>) =>
          buildChartTooltipLabel(item, spec.unit),
      },
    };
  }

  private buildDonutConfig(
    spec: AlloChartSpec,
    palette: ChartPalette,
    tooltip: object
  ): ChartConfiguration<'doughnut'> {
    return {
      type: 'doughnut',
      data: {
        labels: spec.labels,
        datasets: [
          {
            label: spec.series[0].name,
            data: spec.series[0].data,
            backgroundColor: spec.labels.map(
              (_, index) =>
                palette.categorical[index % palette.categorical.length]
            ),
            borderColor: '#ffffff',
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '58%',
        color: palette.text,
        plugins: {
          legend: {
            position: this.isNarrowViewport() ? 'bottom' : 'right',
            labels: { color: palette.text, boxWidth: 12, padding: 10 },
          },
          tooltip,
        },
      },
      plugins: [valueLabelPlugin(spec)],
    };
  }

  private buildLineConfig(
    spec: AlloChartSpec,
    palette: ChartPalette,
    tooltip: object
  ): ChartConfiguration<'line'> {
    // Two-tone for period-over-period comparisons, full ramp beyond that.
    const colors = spec.series.length <= 2 ? palette.duo : palette.categorical;

    const datasets: ChartDataset<'line'>[] = spec.series.map(
      (entry, index) => ({
        label: entry.name,
        data: entry.data,
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length],
        tension: 0.35,
        pointRadius: 2,
        borderWidth: 2,
        fill: false,
      })
    );

    return {
      type: 'line',
      data: { labels: spec.labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        color: palette.text,
        plugins: {
          legend: {
            display: spec.series.length > 1,
            labels: { color: palette.text, boxWidth: 12, padding: 10 },
          },
          tooltip,
        },
        scales: {
          x: {
            grid: { color: palette.grid },
            ticks: { color: palette.text },
          },
          y: {
            grid: { color: palette.grid },
            ticks: {
              color: palette.text,
              callback(tickValue) {
                return `${this.getLabelForValue(Number(tickValue))}${spec.unit}`;
              },
            },
          },
        },
      },
      plugins: [valueLabelPlugin(spec)],
    };
  }

  private buildBarConfig(
    spec: AlloChartSpec,
    palette: ChartPalette,
    tooltip: object
  ): ChartConfiguration<'bar'> {
    // Long category names or many of them read far better horizontally.
    const horizontal =
      spec.labels.some(label => label.length > LONG_LABEL_CHARS) ||
      spec.labels.length > MANY_CATEGORIES;
    const multiSeries = spec.series.length > 1;
    const colors = multiSeries
      ? spec.series.length <= 2
        ? palette.duo
        : palette.categorical
      : palette.categorical;

    const datasets: ChartDataset<'bar'>[] = spec.series.map((entry, index) => ({
      label: entry.name,
      data: entry.data,
      backgroundColor: multiSeries
        ? colors[index % colors.length]
        : spec.labels.map(
            (_, labelIndex) =>
              palette.categorical[labelIndex % palette.categorical.length]
          ),
      borderRadius: 4,
      maxBarThickness: 42,
    }));

    return {
      type: 'bar',
      data: { labels: spec.labels, datasets },
      options: {
        indexAxis: horizontal ? 'y' : 'x',
        responsive: true,
        maintainAspectRatio: false,
        color: palette.text,
        // Reserve room for the end-of-bar value labels.
        layout: {
          padding: horizontal ? { right: 34 } : { top: 16 },
        },
        plugins: {
          legend: {
            display: multiSeries,
            labels: { color: palette.text, boxWidth: 12, padding: 10 },
          },
          tooltip,
        },
        scales: {
          x: {
            grid: { color: palette.grid, display: horizontal },
            ticks: { color: palette.text },
          },
          y: {
            grid: { color: palette.grid, display: !horizontal },
            ticks: { color: palette.text },
          },
        },
      },
      plugins: [valueLabelPlugin(spec)],
    };
  }
}
